import { TextSelection } from '@bangle.io/prosemirror-plugins';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import type { PmEditorService } from '../pm-editor-service';
import { useEditorCoreServices } from '../use-editor-core-services';
import {
  availableEditorContextActions,
  type EditorContextAction,
} from './editor-context-actions';
import {
  FLOATING_INITIAL_STYLE,
  useFloatingPosition,
} from './use-floating-position';
import { useOutsidePointer } from './use-outside-pointer';

type EditorView = NonNullable<ReturnType<PmEditorService['getEditor']>>;
type Extensions = PmEditorService['extensions'];

const MENU_ITEM_SELECTOR = '[role="menuitem"]:not([disabled])';

type OpenMenu = {
  /** Viewport point the menu is anchored to — the right-click itself. */
  x: number;
  y: number;
  view: EditorView;
  actions: EditorContextAction[];
};

/**
 * Right-click menu for the editor.
 *
 * Right-click never moves the ProseMirror selection, so a click either lands
 * in a selection already in progress — which is what makes "Sort A-Z" useful
 * — or places the caret that the actions would then act on. The menu opens
 * only when an action would change the document, which is also what keeps the
 * browser's own menu for paragraphs, headings and code blocks.
 *
 * The listener sits on the document rather than on the editor's own element:
 * that node is the ProseMirror mount, so React does not own its children, and
 * a document listener can decide per event whether to swallow the browser menu.
 */
export function EditorContextMenu({ editorName }: { editorName: string }) {
  const { editorEngine } = useEditorCoreServices();
  const [menu, setMenu] = useState<OpenMenu>();

  const dismiss = useCallback(() => setMenu(undefined), []);

  useEffect(() => {
    const handleContextMenu = (event: MouseEvent) => {
      const editorView = editorEngine.getEditor(editorName);
      const target = event.target;

      if (
        !editorView ||
        !(target instanceof Node) ||
        !editorView.dom.contains(target)
      ) {
        return;
      }

      // On phones/tablets, long-press is how users open the native Copy/Paste
      // callout. Claiming the event for list-sort actions would steal that.
      if (
        typeof window !== 'undefined' &&
        window.matchMedia('(pointer: coarse)').matches
      ) {
        return;
      }

      const coords = editorView.posAtCoords({
        left: event.clientX,
        top: event.clientY,
      });
      if (!coords) {
        return;
      }

      const { selection } = editorView.state;
      const insideSelection =
        !selection.empty &&
        coords.pos >= selection.from &&
        coords.pos <= selection.to;

      if (!insideSelection) {
        const caret = TextSelection.near(
          editorView.state.doc.resolve(coords.pos),
        );
        if (!selection.eq(caret)) {
          editorView.dispatch(editorView.state.tr.setSelection(caret));
        }
      }

      const ext = editorEngine.extensions;
      const actions = availableEditorContextActions(ext, editorView.state);

      // Only claim the event when something here would act on it; otherwise
      // the browser menu stays available.
      if (actions.length === 0) {
        return;
      }

      event.preventDefault();
      // The formatting toolbar is anchored to the same selection and would
      // otherwise sit underneath this menu.
      ext.selectionMenu.command.dismissSelectionMenu()(
        editorView.state,
        editorView.dispatch,
        editorView,
      );

      setMenu({
        actions,
        view: editorView,
        x: event.clientX,
        y: event.clientY,
      });
    };

    document.addEventListener('contextmenu', handleContextMenu);
    return () => document.removeEventListener('contextmenu', handleContextMenu);
  }, [editorEngine, editorName]);

  if (!menu) {
    return null;
  }

  return (
    <EditorContextMenuPopup
      ext={editorEngine.extensions}
      menu={menu}
      onDismiss={dismiss}
    />
  );
}

function EditorContextMenuPopup({
  ext,
  menu,
  onDismiss,
}: {
  ext: Extensions;
  menu: OpenMenu;
  onDismiss: () => void;
}) {
  const popupRef = useRef<HTMLDivElement | null>(null);
  const { view } = menu;
  const ownerDocument = view.dom.ownerDocument;

  const anchorEl = useCallback(
    () => ({
      contextElement: view.dom,
      getBoundingClientRect: () => new DOMRect(menu.x, menu.y, 0, 0),
    }),
    [menu.x, menu.y, view],
  );

  // Focus can only land on an element that is rendered, and the popup stays
  // `display: none` until floating-ui has measured it, so the first item is
  // focused from the positioning callback rather than from an effect. Only the
  // first one: repositioning must not steal focus back from the item the user
  // has already arrowed to.
  const focusedRef = useRef(false);
  const focusFirstItem = () => {
    if (focusedRef.current) return;
    focusedRef.current = true;
    // `preventScroll` because the scroll listener below treats any scrolling
    // as a dismissal, and taking focus can otherwise scroll the menu into
    // view at the same moment it opens.
    popupRef.current
      ?.querySelector<HTMLElement>(MENU_ITEM_SELECTOR)
      ?.focus({ preventScroll: true });
  };

  const floatingRef = useFloatingPosition({
    show: true,
    anchorEl,
    boundaryElement: view.dom,
    onPositioned: focusFirstItem,
    placement: 'bottom-start',
  });

  const setPopupRefs = (node: HTMLDivElement | null) => {
    popupRef.current = node;
    floatingRef.current = node;
  };

  // Taking focus back only when this menu dismissed itself keeps a click
  // elsewhere going where the user pointed it.
  const close = (refocus: boolean) => {
    onDismiss();
    if (refocus) {
      view.focus();
    }
  };

  useOutsidePointer({
    enabled: true,
    ownerDocument,
    popupRef,
    onOutside: () => close(false),
  });

  // The anchor is fixed at the click point, so a scroll would leave the menu
  // hovering over unrelated text.
  useEffect(() => {
    const onScroll = () => {
      onDismiss();
      view.focus();
    };
    const onResize = onScroll;

    ownerDocument.addEventListener('scroll', onScroll, true);
    ownerDocument.defaultView?.addEventListener('resize', onResize);

    return () => {
      ownerDocument.removeEventListener('scroll', onScroll, true);
      ownerDocument.defaultView?.removeEventListener('resize', onResize);
    };
  }, [onDismiss, ownerDocument, view]);

  // Focus walking out of the menu (Tab through the editor, a stray click
  // handled elsewhere, the window losing focus) must not leave it stranded.
  useEffect(() => {
    const onFocusOut = (event: FocusEvent) => {
      const next = event.relatedTarget;
      if (!(next instanceof Node) || !popupRef.current?.contains(next)) {
        onDismiss();
      }
    };

    ownerDocument.addEventListener('focusout', onFocusOut, true);
    return () =>
      ownerDocument.removeEventListener('focusout', onFocusOut, true);
  }, [onDismiss, ownerDocument]);

  const runAction = (action: EditorContextAction) => {
    action.command(ext)(view.state, view.dispatch, view);
    close(true);
  };

  const moveFocus = (key: 'ArrowDown' | 'ArrowUp' | 'Home' | 'End') => {
    const items = Array.from(
      popupRef.current?.querySelectorAll<HTMLElement>(MENU_ITEM_SELECTOR) ?? [],
    );
    if (items.length === 0) return;

    const active = ownerDocument.activeElement;
    const current = active instanceof HTMLElement ? items.indexOf(active) : -1;
    const last = items.length - 1;
    const nextIndex =
      key === 'Home'
        ? 0
        : key === 'End'
          ? last
          : key === 'ArrowDown'
            ? current < 0
              ? 0
              : (current + 1) % items.length
            : current < 0
              ? last
              : (current - 1 + items.length) % items.length;

    items[nextIndex]?.focus();
  };

  return (
    <div ref={setPopupRefs} className="w-max" style={FLOATING_INITIAL_STYLE}>
      <div
        aria-label={t.app.editor.contextMenu.label}
        className="z-50 min-w-32 overflow-hidden rounded-md border border-border bg-popover p-1 text-popover-foreground shadow-lg"
        data-editor-context-menu-root="true"
        onContextMenu={(event) => event.preventDefault()}
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            event.preventDefault();
            event.stopPropagation();
            close(true);
            return;
          }

          if (
            event.key === 'ArrowDown' ||
            event.key === 'ArrowUp' ||
            event.key === 'Home' ||
            event.key === 'End'
          ) {
            event.preventDefault();
            moveFocus(event.key);
            return;
          }

          if (event.key === 'Tab') {
            event.preventDefault();
            close(true);
          }
        }}
        role="menu"
      >
        {menu.actions.map((action) => (
          <ContextMenuAction
            action={action}
            key={action.id}
            onRun={() => runAction(action)}
          />
        ))}
      </div>
    </div>
  );
}

function ContextMenuAction({
  action,
  onRun,
}: {
  action: EditorContextAction;
  onRun: () => void;
}) {
  const Icon = action.icon;
  const label = action.title();

  return (
    <button
      aria-label={label}
      className="flex h-7 w-full items-center gap-2 rounded-sm px-2 text-left text-xs outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:bg-accent focus-visible:text-accent-foreground"
      onClick={onRun}
      role="menuitem"
      type="button"
    >
      <Icon aria-hidden className="size-3.5" />
      {label}
    </button>
  );
}
